import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
    CreateVisitaRequest,
    CrearDescargaMasivaRequest,
    CrearDescargaMasivaResponse,
    DescargaMasiva,
    DescargaMasivaActiva,
    EnlaceDescargaMasiva,
    ErroresDescargaMasiva,
    EstadoDescargaMasiva,
    VisitaResponse
} from '../pages/Visitas/consts/visitasTypes';
import { DatosSiniestroApi } from '../pages/Visitas/consts/types';
import { transformVisitasResponse } from '../pages/Visitas/consts/visitasTransform';
import { VisitaEstado } from '../pages/Visitas/consts/visitasStatus';

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const visitasApi = createApi({
    reducerPath: 'visitas',
    tagTypes: ['Visitas', 'Verosimilitud'] as const,
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        createVisita: builder.mutation<VisitaResponse, CreateVisitaRequest>({
            query: (body) => ({
                url: `/visitas`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['Visitas']
        }),
        patchVisita: builder.mutation<
            void,
            {
                idVisita: number;
                body: {
                    observacionAlta: string;
                    idsTipoVisita: number[];
                    fechaLimite: string;
                    idsEstablecimiento?: number[];
                    idPreventor?: number;
                    idProveedor?: number;
                    idSiniestro?: number;
                };
            }
        >({
            query: ({ idVisita, body }) => ({
                url: `/visitas/${idVisita}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: ['Visitas']
        }),
        getVisitas: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: params
            }),
            providesTags: ['Visitas'],
            transformResponse: transformVisitasResponse
        }),
        getVisitasRoam: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: { ...params, siniestroIdNotnull: true }
            }),
            providesTags: ['Visitas'],
            transformResponse: transformVisitasResponse
        }),
        getVisitaById: builder.query<any, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}`,
                method: 'GET'
            }),
            providesTags: ['Visitas']
        }),
        getVisitasIndicators: builder.query({
            query: () => ({
                url: `/visitas/indicadores/total`,
                method: 'GET'
            }),
            providesTags: ['Visitas'],
            transformResponse: (response: { data: any }) => response?.data,
            keepUnusedDataFor: 5
        }),
        downloadConstanciaCapacitacion: builder.mutation<{ data: string }, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/constancia-capacitacion`,
                method: 'POST'
            })
        }),
        downloadActividadDocumento: builder.mutation<
            { data: { presignedUrl: string } },
            { idVisita: number; idDocumento: number; idActividad?: number }
        >({
            query: ({ idVisita, idActividad, idDocumento }) => ({
                url: `/visitas/${idVisita}/documentos`,
                method: 'GET',
                params: idActividad ? { idActividad, idDocumento } : { idDocumento }
            })
        }),

        deleteActividadDocumento: builder.mutation({
            query: ({ idVisita, idActividad, idDocumento }) => ({
                url: `/visitas/${idVisita}/actividades/${idActividad}/documentos/${idDocumento}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Visitas']
        }),
        deleteAdditionalDocumento: builder.mutation<
            void,
            { idVisita: number; idDocumento: number }
        >({
            query: ({ idVisita, idDocumento }) => ({
                url: `/visitas/${idVisita}/documentos/${idDocumento}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Visitas']
        }),
        getTiposVisitas: builder.query({
            query: () => ({
                url: `/visitas/tipos-visita`,
                method: 'GET'
            }),
            providesTags: ['Visitas']
        }),
        getMotivosFallaVisita: builder.query<
            { data: { id: number; codigo: string; descripcion: string }[] },
            void
        >({
            query: () => ({
                url: `/visitas/motivos-falla`,
                method: 'GET'
            }),
            providesTags: ['Visitas']
        }),
        patchVisitaEstado: builder.mutation<
            void,
            { idVisita: number; estado: number; observacion: string }
        >({
            query: ({ idVisita, estado, observacion }) => ({
                url: `/visitas/${idVisita}`,
                method: 'PATCH',
                body: { estado, observacion }
            }),
            invalidatesTags: ['Visitas']
        }),
        patchProgramVisit: builder.mutation<void, { idVisita: number; fecha: string }>({
            query: ({ idVisita, fecha }) => ({
                url: `/visitas/${idVisita}/programacion`,
                method: 'PATCH',
                body: { fecha }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        patchProgramVisits: builder.mutation<void, { idsVisita: number[]; fecha: string }>({
            query: ({ idsVisita, fecha }) => ({
                url: `/visitas/programacion`,
                method: 'PATCH',
                body: { idsVisita, fecha }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        patchVisitaFechaVisita: builder.mutation<
            void,
            { idVisita: number; fechaVisita: Date }
        >({
            query: ({ idVisita, fechaVisita }) => ({
                url: `/visitas/${idVisita}/fecha-visita`,
                method: 'PATCH',
                body: { fechaVisita }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),

        patchEjecutionVisit: builder.mutation<
            void,
            {
                idVisita: number;
                esFallida: boolean;
                idMotivoFallaVisita?: number | null;
                observacionFalla?: string;
                totalPersonal: number | null;
                administrativos: number | null;
                personalProduccion: number | null;
                otrosPersonal?: number | null;
                seguridadHigiene: boolean | null;
                profesional: string;
                matricula: string;
                matriculaNoInforma?: boolean;
                emailHys?: string;
                emailHysNoInforma?: boolean;
                descripcionEstablecimiento?: string;
                idTipoVisita: number | null;
                idsTipoVisita: number[];
                idSubTipoVisita: number | null;
                fechaLimite: string | null;
                observacion: string;
                firmaPreventor: string;
                aclaracionPreventor?: string;
                dniPreventor?: string;
                cargoPreventor?: string;
                firmaResponsable: string;
                aclaracionResponsable?: string;
                dniResponsable?: string;
                cargoResponsable?: string;
                nombreEntrevistado?: string;
                dniEntrevistado?: string;
                caracterEntrevistado?: string;
                telefonoEntrevistado?: string;
                emailEntrevistado?: string;
                estado: number;
                idPresentacion: number | null;
                esBorrador?: boolean;
            }
        >({
            query: ({ idVisita, ...rest }) => {
                // En la fallida, la firma del responsable y los datos de quien recibió al
                // preventor son opcionales (SMP-416): van solo si se cargaron, porque el back
                // valida el formato de todo lo que llega, vacío incluido. La observación es
                // obligatoria al cerrar (SMP-546), pero en el borrador puede estar vacía.
                const opcionalesFallida = Object.fromEntries(
                    (
                        [
                            'observacion',
                            'observacionFalla',
                            'firmaResponsable',
                            'aclaracionResponsable',
                            'dniResponsable',
                            'cargoResponsable',
                            'nombreEntrevistado',
                            'dniEntrevistado',
                            'caracterEntrevistado',
                            'telefonoEntrevistado',
                            'emailEntrevistado'
                        ] as const
                    )
                        .map((campo) => [campo, rest[campo]?.trim()])
                        .filter(([, valor]) => Boolean(valor))
                );
                const filteredBody = rest.esFallida
                    ? {
                          esFallida: rest.esFallida,
                          firmaPreventor: rest.firmaPreventor,
                          aclaracionPreventor: rest.aclaracionPreventor,
                          dniPreventor: rest.dniPreventor,
                          cargoPreventor: rest.cargoPreventor,
                          idMotivoFallaVisita: rest.idMotivoFallaVisita,
                          ...opcionalesFallida,
                          ...(rest.esBorrador ? { esBorrador: rest.esBorrador } : {})
                      }
                    : rest;

                return {
                    url: `/visitas/${idVisita}/registro`,
                    method: 'PATCH',
                    body: filteredBody
                };
            },
            invalidatesTags: ['Visitas']
        }),
        getVerosimilitudById: builder.query<any, { idVisita: number; idVerosimilitud: number }>({
            query: ({ idVisita, idVerosimilitud }) => ({
                url: `/visitas/${idVisita}/verosimilitud/${idVerosimilitud}`,
                method: 'GET'
            }),
            providesTags: (result, error, { idVisita, idVerosimilitud }) => [
                { type: 'Verosimilitud', id: `${idVisita}-${idVerosimilitud}` }
            ]
        }),
        getSeguimientoVerosimilitud: builder.query<any, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/seguimiento-verosimilitud`,
                method: 'GET'
            }),
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: (result, error, { idVisita }) => [
                { type: 'Verosimilitud', id: `seguimiento-${idVisita}` }
            ]
        }),
        createVerosimilitud: builder.mutation<
            void,
            {
                idVisita: number;
                respuestas: {
                    idPregunta: number;
                    idTipoRespuesta: number;
                    fechaRegularizacion?: string;
                    fechaSeguimiento?: string;
                    observacion?: string;
                }[];
            }
        >({
            query: ({ idVisita, ...body }) => ({
                url: `/visitas/${idVisita}/verosimilitud`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['Visitas']
        }),
        getRecomendacionesVerosimilitud: builder.query<
            {
                data: {
                    id: number;
                    recomendaciones: {
                        id: number;
                        idTipoRespuesta: number;
                        texto: string;
                        orden?: number;
                    }[];
                }[];
            },
            void
        >({
            query: () => ({
                url: `/visitas/recomendaciones-verosimilitud`,
                method: 'GET'
            })
        }),
        getTechnicalVisitQuestions: builder.query<any, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/visita-tecnica/preguntas`,
                method: 'GET'
            })
        }),
        getTechnicalVisit: builder.query<any, { idVisita: number; idVisitaTecnica: number }>({
            query: ({ idVisita, idVisitaTecnica }) => ({
                url: `/visitas/${idVisita}/visita-tecnica/${idVisitaTecnica}`,
                method: 'GET'
            })
        }),
        createTechnicalVisit: builder.mutation<
            any,
            {
                idVisita: number;
                respuestas: {
                    idSeccion: number;
                    idTipoRespuesta: number;
                    observacion?: string;
                }[];
            }
        >({
            query: ({ idVisita, respuestas }) => ({
                url: `/visitas/${idVisita}/visita-tecnica`,
                method: 'POST',
                body: { respuestas }
            }),
            invalidatesTags: ['Visitas']
        }),
        patchTechnicalVisit: builder.mutation<
            any,
            {
                idVisita: number;
                idVisitaTecnica: number;
                respuestas: {
                    idSeccion: number;
                    idTipoRespuesta: number;
                    observacion?: string;
                }[];
            }
        >({
            query: ({ idVisita, idVisitaTecnica, respuestas }) => ({
                url: `/visitas/${idVisita}/visita-tecnica/${idVisitaTecnica}`,
                method: 'PATCH',
                body: { respuestas }
            }),
            invalidatesTags: ['Visitas']
        }),
        downloadTechnicalVisitPdf: builder.mutation<
            { data?: string } | string,
            { idVisita: number }
        >({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/pdf-visita-tecnica`,
                method: 'POST'
            })
        }),
        patchVerosimilitud: builder.mutation<
            void,
            {
                idVisita: number;
                idVerosimilitud: number;
                respuestas: {
                    idPregunta: number;
                    idTipoRespuesta: number;
                    fechaRegularizacion?: string;
                    fechaSeguimiento?: string;
                    observacion?: string;
                }[];
            }
        >({
            query: ({ idVisita, idVerosimilitud, ...body }) => ({
                url: `/visitas/${idVisita}/verosimilitud/${idVerosimilitud}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (result, error, { idVisita, idVerosimilitud }) => [
                { type: 'Verosimilitud', id: `${idVisita}-${idVerosimilitud}` }
            ]
        }),

        patchVerosimilitudData: builder.mutation<
            void,
            {
                idVisita: number;
                idPreventor: number;
                observacion: string;
                idPresentacion: number;
                respuestas: {
                    idSeccion: number;
                    idTipoRepuesta: number;
                    fechaRegulacion?: string;
                }[];
            }
        >({
            query: ({ idVisita, ...body }) => ({
                url: `/visitas/${idVisita}/verosimilitud`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['Visitas']
        }),

        patchVerosimilitudVisit: builder.mutation<
            void,
            {
                idVisita: number;
                respuestas: { idSeccion: number; idTipoRepuesta: number }[];
            }
        >({
            query: ({ idVisita, ...body }) => ({
                url: `/visitas/${idVisita}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: ['Visitas']
        }),
        deleteVerosimilitudVisit: builder.mutation<
            void,
            { idVisita: number; idPresentacion: number }
        >({
            query: ({ idVisita, idPresentacion }) => ({
                url: `/visitas/verosimilitud`,
                method: 'DELETE',
                body: { idVisita, idPresentacion }
            }),
            invalidatesTags: ['Visitas']
        }),
        downloadVisitaActa: builder.mutation<
            { data: string },
            {
                idVisita: number;
                idTipoVisita: number;
                tipoActa: number;
                blank?: boolean;
                // Pide el acta de una falla puntual (evento del log de auditoría).
                idEventoFalla?: number;
                datos?: {
                    entrevistado?: {
                        nombre?: string;
                        dni?: string;
                        caracter?: string;
                        telefono?: string;
                        email?: string;
                    };
                };
            }
        >({
            query: ({ idVisita, idTipoVisita, tipoActa, blank, idEventoFalla, datos }) => ({
                url: `/visitas/${idVisita}/acta`,
                method: 'POST',
                body: {
                    idTipoVisita,
                    tipoActa,
                    ...(datos ? { datos } : {}),
                    ...(blank ? { blank: true } : {}),
                    ...(idEventoFalla != null ? { idEventoFalla } : {})
                }
            })
        }),

        // PDF "Informe de investigacion de accidente" desde la visita de investigacion aprobada
        // (SMP-401). Lleva al pie las firmas de la visita; solo para el Preventor.
        descargarPdfInvestigacionVisita: builder.mutation<string, number>({
            query: (idVisita) => ({
                url: `/visitas/${idVisita}/formulario-srt/pdf`,
                method: 'POST',
                body: { vacio: false }
            }),
            transformResponse: (response: { data: string }) => response?.data
        }),

        // PDF "Informe de investigacion de accidente" desde el siniestro (SMP-399). El backend agrega
        // las firmas si la investigacion se precargo desde una visita aprobada; la completitud del
        // formulario la valida el front, igual que el TXT.
        descargarPdfInvestigacionSiniestro: builder.mutation<string, number>({
            query: (idSiniestro) => ({
                url: `/visitas/siniestros/${idSiniestro}/formulario-srt/pdf`,
                method: 'POST'
            }),
            transformResponse: (response: { data: string }) => response?.data
        }),

        getVisitTypes: builder.query({
            query: () => ({
                url: `/visitas/tipos-visita`,
                method: 'GET'
            })
        }),
        getAllVisits: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params:
                    params?.estado !== undefined
                        ? {
                              ...params,
                              estado: undefined,
                              idsEstado: [params.estado]
                          }
                        : params
            }),
            providesTags: ['Visitas']
        }),
        getRejectedVisits: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: params?.estado
                    ? { ...params, idsEstado: [params?.estado] }
                    : { ...params, idsEstado: [VisitaEstado.RECHAZADO, VisitaEstado.SIN_ASIGNAR] }
            }),
            providesTags: ['Visitas']
        }),

        getPendingVisits: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: { ...params, idsEstado: VisitaEstado.PENDIENTE }
            }),
            providesTags: ['Visitas']
        }),
        getProgrammedVisits: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: { ...params, idsEstado: VisitaEstado.PROGRAMADA }
            }),
            providesTags: ['Visitas']
        }),
        getInProgressVisits: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: { ...params, idsEstado: VisitaEstado.EN_PROCESO }
            }),
            providesTags: ['Visitas']
        }),
        getInRevisionVisits: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: { ...params, idsEstado: VisitaEstado.REALIZADA }
            }),
            providesTags: ['Visitas']
        }),
        getClosedVisits: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: params?.estado
                    ? { ...params, idsEstado: [params?.estado] }
                    : {
                          ...params,
                          idsEstado: [
                              VisitaEstado.FALLIDA,
                              VisitaEstado.PRESENTADO,
                              VisitaEstado.RECHAZADO,
                              VisitaEstado.CANCELADA
                          ]
                      }
            }),
            providesTags: ['Visitas']
        }),
        getClosedVisitsBO: builder.query({
            query: (params) => ({
                url: `/visitas`,
                method: 'GET',
                params: params?.estado
                    ? { ...params, idsEstado: [params?.estado] }
                    : {
                          ...params,
                          idsEstado: [
                              VisitaEstado.FALLIDA,
                              VisitaEstado.PRESENTADO,
                              VisitaEstado.CANCELADA
                          ]
                      }
            }),
            providesTags: ['Visitas']
        }),
        getPreventors: builder.query({
            query: (params) => ({
                url: `/visitas/preventores`,
                method: 'GET',
                params: { ...params }
            })
        }),
        assignPreventor: builder.mutation({
            query: ({
                preventorID,
                body
            }: {
                preventorID: number;
                body: { idsVisita: number[]; observacion: string };
            }) => ({
                url: `/visitas/preventores/${preventorID}/asignacion`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        reassignPreventor: builder.mutation({
            query: ({
                preventorID,
                body
            }: {
                preventorID: number;
                body: {
                    idsVisita: number[];
                    observacion: string;
                    motivo: string;
                };
            }) => ({
                url: `/visitas/preventores/${preventorID}/reasignacion`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        presentVisit: builder.mutation({
            query: ({ visitId }: { visitId: number }) => ({
                url: `/visitas/${visitId}/presentacion`,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        cancelVisit: builder.mutation<void, { visitId: number; observacion?: string }>({
            query: ({ visitId, observacion = '' }) => ({
                url: `/visitas/cancelacion`,
                method: 'PATCH',
                body: { observacion, idsVisita: [visitId] }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        cancelVisits: builder.mutation<void, { idsVisita: number[]; observacion: string }>({
            query: ({ idsVisita, observacion }) => ({
                url: `/visitas/cancelacion`,
                method: 'PATCH',
                body: { observacion, idsVisita }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        rejectVisit: builder.mutation<
            void,
            {
                visitId: number;
                motivo: string;
            }
        >({
            query: ({ visitId, motivo }) => ({
                url: `/visitas/${visitId}/rechazo`,
                method: 'PATCH',
                body: { motivo }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),

        sendVisitToRevision: builder.mutation({
            query: ({ visitId, body }: { visitId: number; body: { observacion: string } }) => ({
                url: `/visitas/${visitId}/revision`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Visitas'])
        }),
        uploadAdditionalDocument: builder.mutation<any, { idVisita: number; file: File }>({
            query: ({ idVisita, file }) => {
                const formData = new FormData();
                formData.append('file', file);

                return {
                    url: `/visitas/${idVisita}/documentos`,
                    method: 'POST',
                    body: formData
                };
            },
            invalidatesTags: ['Visitas']
        }),
        getAdditionalDocuments: builder.query({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/documentos`,
                method: 'GET'
            })
        }),
        getVisitaSiniestro: builder.query<{ data: DatosSiniestroApi }, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/siniestro`,
                method: 'GET'
            })
        }),
        getLogAuditoriaVisita: builder.query<{ data: any[] }, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/log-auditoria`,
                method: 'GET'
            })
        }),
        uploadActivityDocument: builder.mutation<
            any,
            { idVisita: number; idActividad: number; idTipoArchivo: number; file: File }
        >({
            query: ({ idVisita, idActividad, idTipoArchivo, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('idTipoArchivo', String(idTipoArchivo));

                return {
                    url: `/visitas/${idVisita}/actividades/${idActividad}/documentos`,
                    method: 'POST',
                    body: formData
                };
            }
        }),
        presentActivityRgrl: builder.mutation<any, { idVisita: number; idActividad: number }>({
            query: ({ idVisita, idActividad }) => ({
                url: `/visitas/${idVisita}/actividades/${idActividad}/documentos`,
                method: 'POST',
                body: {
                    formulario: true
                }
            })
        }),
        saveSeguimientoVerosimilitud: builder.mutation<
            any,
            { idVisita: number; respuestas: { idPregunta: number; idTipoRespuesta: number }[] }
        >({
            query: ({ idVisita, respuestas }) => ({
                url: `/visitas/${idVisita}/seguimiento-verosimilitud`,
                method: 'PUT',
                body: { respuestas }
            }),
            invalidatesTags: (result, error, { idVisita }) => [
                { type: 'Verosimilitud', id: `seguimiento-${idVisita}` }
            ]
        }),
        presentActividadSeguimientoVerosimilitud: builder.mutation<
            any,
            { idVisita: number; idActividad: number }
        >({
            query: ({ idVisita, idActividad }) => ({
                url: `/visitas/${idVisita}/actividades/${idActividad}/documentos`,
                method: 'POST',
                body: { formulario: true }
            }),
            invalidatesTags: ['Visitas']
        }),
        presentActivityVerosimilitud: builder.mutation<
            any,
            { idVisita: number; idActividad: number }
        >({
            query: ({ idVisita, idActividad }) => ({
                url: `/visitas/${idVisita}/actividades/${idActividad}/documentos`,
                method: 'POST',
                body: {
                    formulario: true
                }
            })
        }),
        getFormularioSrtVisita: builder.query<any, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/formulario-srt`,
                method: 'GET'
            }),
            transformResponse: (response: any) => response?.data ?? response,
            providesTags: ['Visitas']
        }),
        patchFormularioSrtVisita: builder.mutation<void, { idVisita: number; body: any }>({
            query: ({ idVisita, body }) => ({
                url: `/visitas/${idVisita}/formulario-srt`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: ['Visitas']
        }),
        getDatosParametricosSrtVisita: builder.query<any, { idVisita: number }>({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/formulario-srt/datos-parametricos`,
                method: 'GET'
            }),
            transformResponse: (response: any) => response?.data ?? response,
            keepUnusedDataFor: 300
        }),
        exportVisitas: builder.mutation<{ type: string; data: number[] }, Record<string, any>>({
            query: (body) => ({
                url: '/visitas/exportar',
                method: 'POST',
                body
            })
        }),
        descargarExpedienteZip: builder.mutation<
            { data: { presignedUrl: string; archivosAusentes: string[] } },
            { idVisita: number }
        >({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/expediente-zip`,
                method: 'GET'
            })
        }),
        // ZIP con la "Documentación adicional" de la visita (SMP-551).
        descargarDocumentacionAdicionalZip: builder.mutation<
            { data: { presignedUrl: string } },
            { idVisita: number }
        >({
            query: ({ idVisita }) => ({
                url: `/visitas/${idVisita}/documentacion-adicional-zip`,
                method: 'GET'
            })
        }),
        downloadSeguimientoVerosimilitudPdf: builder.mutation<{ data: string }, { idActividad: number }>({
            query: ({ idActividad }) => ({
                url: `/visitas/${idActividad}/pdf-seguimiento-verosimilitud`,
                method: 'POST'
            })
        }),
        // Descarga masiva de PDF de visitas presentadas (SMP-512)
        crearDescargaMasiva: builder.mutation<
            { data: CrearDescargaMasivaResponse },
            CrearDescargaMasivaRequest
        >({
            query: (body) => ({ url: '/visitas/descargas-masivas', method: 'POST', body })
        }),
        getDescargaMasiva: builder.query<{ data: DescargaMasiva }, string>({
            query: (idDescarga) => `/visitas/descargas-masivas/${idDescarga}`,
            keepUnusedDataFor: 0
        }),
        getDescargaMasivaActiva: builder.query<{ data: DescargaMasivaActiva }, void>({
            query: () => '/visitas/descargas-masivas/activa',
            keepUnusedDataFor: 0
        }),
        getEnlaceDescargaMasiva: builder.mutation<{ data: EnlaceDescargaMasiva }, string>({
            query: (idDescarga) => ({
                url: `/visitas/descargas-masivas/${idDescarga}/enlace`,
                method: 'GET'
            })
        }),
        getErroresDescargaMasiva: builder.mutation<{ data: ErroresDescargaMasiva }, string>({
            query: (idDescarga) => ({
                url: `/visitas/descargas-masivas/${idDescarga}/errores`,
                method: 'GET'
            })
        }),
        cancelarDescargaMasiva: builder.mutation<
            { data: { estado: EstadoDescargaMasiva } },
            string
        >({
            query: (idDescarga) => ({
                url: `/visitas/descargas-masivas/${idDescarga}/cancelar`,
                method: 'POST'
            })
        })
    })
});

export const {
    useCrearDescargaMasivaMutation,
    useGetDescargaMasivaQuery,
    useLazyGetDescargaMasivaActivaQuery,
    useGetEnlaceDescargaMasivaMutation,
    useGetErroresDescargaMasivaMutation,
    useCancelarDescargaMasivaMutation,
    useCreateVisitaMutation,
    usePatchVisitaMutation,
    useGetVisitasQuery,
    useLazyGetVisitasRoamQuery,
    useGetVisitaByIdQuery,
    useLazyGetVisitaByIdQuery,
    useLazyGetVisitasQuery,
    useGetVisitasIndicatorsQuery,
    useGetTiposVisitasQuery,
    useGetMotivosFallaVisitaQuery,
    usePatchVisitaEstadoMutation,
    usePatchEjecutionVisitMutation,
    useCreateVerosimilitudMutation,
    useGetRecomendacionesVerosimilitudQuery,
    useGetTechnicalVisitQuestionsQuery,
    useLazyGetTechnicalVisitQuestionsQuery,
    useGetTechnicalVisitQuery,
    useLazyGetTechnicalVisitQuery,
    useCreateTechnicalVisitMutation,
    usePatchTechnicalVisitMutation,
    useDownloadTechnicalVisitPdfMutation,
    usePatchVerosimilitudVisitMutation,
    usePatchVerosimilitudDataMutation,
    useDeleteVerosimilitudVisitMutation,
    useGetVerosimilitudByIdQuery,
    useLazyGetVerosimilitudByIdQuery,
    useGetSeguimientoVerosimilitudQuery,
    useLazyGetSeguimientoVerosimilitudQuery,
    useDownloadConstanciaCapacitacionMutation,
    useDownloadVisitaActaMutation,
    useDescargarPdfInvestigacionSiniestroMutation,
    useDescargarPdfInvestigacionVisitaMutation,
    useDownloadActividadDocumentoMutation,
    useDeleteActividadDocumentoMutation,
    useDeleteAdditionalDocumentoMutation,
    useGetVisitTypesQuery,
    usePatchProgramVisitMutation,
    usePatchProgramVisitsMutation,
    usePatchVisitaFechaVisitaMutation,
    usePatchVerosimilitudMutation,
    useLazyGetAllVisitsQuery,
    useLazyGetRejectedVisitsQuery,
    useLazyGetPendingVisitsQuery,
    useLazyGetProgrammedVisitsQuery,
    useLazyGetInRevisionVisitsQuery,
    useLazyGetInProgressVisitsQuery,
    useLazyGetClosedVisitsQuery,
    useLazyGetClosedVisitsBOQuery,
    useGetPreventorsQuery,
    useAssignPreventorMutation,
    useReassignPreventorMutation,
    usePresentVisitMutation,
    useCancelVisitMutation,
    useCancelVisitsMutation,
    useRejectVisitMutation,
    useSendVisitToRevisionMutation,
    useUploadAdditionalDocumentMutation,
    useGetAdditionalDocumentsQuery,
    useUploadActivityDocumentMutation,
    usePresentActivityRgrlMutation,
    useSaveSeguimientoVerosimilitudMutation,
    usePresentActividadSeguimientoVerosimilitudMutation,
    usePresentActivityVerosimilitudMutation,
    useLazyGetVisitaSiniestroQuery,
    useGetLogAuditoriaVisitaQuery,
    useLazyGetLogAuditoriaVisitaQuery,
    useGetFormularioSrtVisitaQuery,
    usePatchFormularioSrtVisitaMutation,
    useGetDatosParametricosSrtVisitaQuery,
    useExportVisitasMutation,
    useDescargarExpedienteZipMutation,
    useDescargarDocumentacionAdicionalZipMutation,
    useDownloadSeguimientoVerosimilitudPdfMutation
} = visitasApi;
